import '@fortawesome/fontawesome-free/css/all.css'

import 'hammerjs'
import _ from 'lodash'
import React, { useEffect, useState, useMemo, useRef } from 'react'
import {
    BrowserRouter as Router,
    Routes,
    Route,
    useNavigate,
    useLocation,
} from 'react-router-dom'

import { IxSpotlight } from 'components/spotlight/spotlight'

import notify from 'devextreme/ui/notify'
import { locale } from 'devextreme/localization'
import { useTranslation } from 'react-i18next'

import {
    Login,
    SsoLogin,
    SsoRedirect,
} from 'pages/login'

import { useIxAuth } from 'contexts/auth-context'
import { useIxGlobalFilter } from 'contexts/global-filter-context'
import { useIxTheme, themesList } from 'contexts/theme-context'
import { useSettingsUpdate } from 'hooks'
import { Drawer } from 'layout'
import { Can } from 'components'

import { FullPageSpinner } from 'pages/full-page-spinner'
import { IxGlobalConfirmPopup } from 'components'
import { getJobsData } from './pages/background-jobs'

import client from 'api'

import {setBackgroundJobsData, resetSettings, setSettings as setUserSettings, settingsMenuSelector
} from 'slices/ui'
import { Settings as SettingsEnum } from 'helpers/settings-enum'

import { useAppDispatch } from 'store'

import { routes } from 'routes'

import { useSelector } from 'react-redux'
import { RootState } from 'slices'
import { setJobsInfo } from 'slices/ui'
import { setNotificationsByKeyThunk, updateNotificationThunk } from 'slices/notifications'
import { fetchAllRights, userLoggedIn, userLoggedOut, fetchLNR, fetchActiveLicenses } from 'slices/rights'
import { ProtokollCurrentStatusType } from './pages/background-jobs/background-jobs-columns-enum'

// import utils singleton so we can pass some settings/rights to it
import { utils } from 'utils'
import { License } from 'enums/license-enum'

export const NoAccess = (_: any) => {
    const { user } = useIxAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation()
    const rights = useSelector((state: RootState) => state.rights)

    useEffect(() => {
        const currentUrl = `${location.pathname}`

        if (currentUrl !== '/login' && currentUrl !== '/sso-login' && currentUrl !== '/') {
            localStorage.setItem('redirect_url', `${currentUrl}${location.search}`)
        }

        if (!user) {
            navigate({
                pathname: '/login',
            })
        }
    }, [navigate, location, user])

    if (rights.statusLNR === 'notloaded' || rights.statusLNR === 'loading'){
        return (
            <h3 style={{ marginLeft: '1.5rem' }}> </h3>
        )
    }
    else {
        return (
            <h3 style={{ marginLeft: '1.5rem' }}>{t('global:NoAccess')}</h3>
        )
    }
}

export const AlreadyLoggedIn = (_: any) => {
    const navigate = useNavigate()
    const location = useLocation()

    // prevent buggy redirect to login after logging in (shouldn't happen with normal use but just in case)
    useEffect(() => {
        const redirectUrl = localStorage.getItem('redirect_url')
        localStorage.removeItem('redirect_url')

        if (redirectUrl && redirectUrl !== '/login') {
            navigate(redirectUrl, { state: { redirected: true } })
        } else {
            navigate('/')
        }
    }, [location, navigate])

    return null
}

// Component to fetch the user settings and set them in the corresponding contexts
export const Settings = ({ settings }: any) => {
    const location = useLocation()
    const navigate = useNavigate()
    const { user } = useIxAuth()
    const [firstLoad, setFirstLoad] = useState(true)
    const { i18n } = useTranslation()
    const { setObjectFilter, setFromFilter } = useIxGlobalFilter()
    const { changeTheme } = useIxTheme()
    const updateSetting = useSettingsUpdate()


    useEffect(() => {
        // reset firstLoad on logout
        if (!user && !firstLoad) {
            setFirstLoad(true)
        }
    }, [user, firstLoad])

    // listen for changes in the url and store them in backend
    useEffect(() => {
        if (user) {
            const { pathname, search } = location
            updateSetting('frontend_route', `${pathname}${search}`)
        }
    }, [user, location, updateSetting])

    useEffect(() => {
        if (!settings || !firstLoad) { return }

        // set language
        locale(settings.language)
        i18n.changeLanguage(settings.language)

        // set global filters
        if (settings.object_filter && !isNaN(Number(settings.object_filter))) {
            setObjectFilter(Number(settings.object_filter), false)
        }
        if (settings.timerange_filter === 'None') {
            setFromFilter(null, false)
        } else {
            setFromFilter(settings.timerange_filter, false)
        }

        // set theme
        const theme = themesList.find(t => t.id === settings.theme)
        if (theme && changeTheme) {
            changeTheme(theme.dxName, false)
        }

        // handle redirecting user to where he needs to be, if we're on the login page
        // first is redirect_url if available
        const redirectUrl = localStorage.getItem('redirect_url')
        localStorage.removeItem('redirect_url')

        // if we're not redirected yet make sure we're on the right page
        if (!(location.pathname !== '/login' && location.pathname !== '/sso-login' && location.pathname !== '/') && !(location.state && (location.state as any).redirected)) {
            if (redirectUrl) {
                navigate(redirectUrl, { state: { redirected: true } })
            } else if (settings.frontend_route && (location.pathname === '/' || location.pathname === '/login')) {
                navigate(settings.frontend_route, { state: { redirected: true } })
            } else if (location.pathname === '/login') {
                navigate('/')
            }
        }

        setFirstLoad(false)
    }, [settings, i18n, changeTheme, setObjectFilter, setFromFilter, firstLoad, location, navigate])

    // don't render anything
    return null
}

export const IxJobsUpdater = () => {
    const dispatch = useAppDispatch()

    const { fromFilter } = useIxGlobalFilter()
    const { notifications } = useSelector((state: RootState) => state.notifications)
    const jobsInfo = useSelector((state: RootState) => state.ui.jobsInfo)
    const {value: barsStyle} = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.barsStyle))
    const intervalsId = useRef<any>([])
    const [newJobsData, setNewJobsData] = useState<any>({})

    const expireTimeByStatus: any = useMemo(() => ({
        [ProtokollCurrentStatusType.Running]: 60000,
        [ProtokollCurrentStatusType.Finished]: 120000
    }),[])

    useEffect(() => {
        if (!_.isEmpty(jobsInfo)) {
            Object.keys(jobsInfo).forEach((id: any) => {
                const jobData: any = Object.values(newJobsData).find((job: any) => job.HDR_ID === Number(id))
                if ( jobData?.CURRENTSTATE && jobData?.CURRENTSTATE !== jobsInfo[id].status) {
                    dispatch(setJobsInfo({[id]: {...jobsInfo[id], status: jobData?.CURRENTSTATE}}))
                    notifications.find(n => n.id === id)
                    ? dispatch(updateNotificationThunk({
                        expireTime: expireTimeByStatus[jobData?.CURRENTSTATE],
                        id: jobData?.HDR_ID.toString(),
                        data: jobData
                    }))
                    : dispatch(setNotificationsByKeyThunk(
                        [{
                            type: 'job',
                            name: 'job',
                            msg: jobsInfo[id].msg,
                            expireTime: expireTimeByStatus[jobData?.CURRENTSTATE],
                            data: jobData
                        }],
                        'background-jobs', false
                    ))
                }
            })
        }

    },[newJobsData, dispatch, expireTimeByStatus, fromFilter, jobsInfo, notifications])


    useEffect(() => {
        let interval: any

        if (fromFilter) {
            interval = setInterval(() => {
                getJobsData(fromFilter).then((res) => {
                    dispatch(setBackgroundJobsData(res.data.data))
                })
            }, 600000) // interval-10 minutes
        }

        return () => {
            clearInterval(interval)
        }

    },[fromFilter, dispatch])

    useEffect(() => {
        if (!_.isEmpty(jobsInfo)) {
            Object.keys(jobsInfo).forEach((id: any) => {
                if (!intervalsId.current.includes(id)) {
                    intervalsId.current = [...intervalsId.current, id]

                    const timerStarting = setInterval(() => {
                        client.get(`/api/ixweb/protokoll/?hdr_id=${id}&action=eql`).then((e) => {
                            if (e.data[0].CURRENTSTATE === ProtokollCurrentStatusType.Running) {
                                setNewJobsData((jobsData: any) => ({...jobsData, [id]: e.data[0]}) )
                                clearInterval(timerStarting)

                                const timerRunning = setInterval(() => {
                                    client.get(`/api/ixweb/protokoll/?hdr_id=${id}&action=eql`).then((e) => {
                                        if (e.data[0].CURRENTSTATE === ProtokollCurrentStatusType.Finished || e.data[0].CURRENTSTATE === ProtokollCurrentStatusType.Failed) {
                                            setNewJobsData((jobsData: any) => ({...jobsData, [id]: e.data[0]}) )
                                            clearInterval(timerRunning)
                                        }
                                    })
                                }, 6000)
                            }
                        })
                    }, 2000)
                }
            })
        }

    },[jobsInfo])

    useEffect(() => {
        const rootEl = document.getElementById('root')
        if (rootEl) {
            rootEl.classList.remove('default-bars')
            rootEl.classList.remove('rounded-bars')
            rootEl.classList.remove('super-rounded-bars')
            rootEl.classList.add(barsStyle)
        }
    }, [barsStyle])

    return null
}

export const MainPage = () => {
    const { user } = useIxAuth()
    const [settings, setSettings] = useState<any>(null)

    const dispatch = useAppDispatch()

    const { statusAll: rightsStatus } = useSelector((state: RootState) => state.rights)
    const { settings: userSettings } = useSelector((state: RootState) => state.ui)
    const { value: fieldBorderHeight } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.fieldBorderHeight))
    const { value: compact } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.compact))
    const { value: fieldBorderHoverHeight } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.fieldBorderHoverHeight))
    const { value: fieldBorderColor } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.fieldBorderColor))
    const { value: formFieldType } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.formFieldType))
    const { value: gridHeaderColor } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.gridHeaderColor))
    const { value: gridHeaderTextColor } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.gridHeaderTextColor))
    const { value: gridRowColor } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.gridRowColor))
    const { value: gridRowTextColor } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.gridRowTextColor))
    const { value: gridBordersColor } = useSelector((state: RootState) => settingsMenuSelector(state, SettingsEnum.gridBordersColor))

    useEffect(() => {
        const classes = [
            {value: fieldBorderColor, class: `field-border-${fieldBorderColor}`},
            {value: formFieldType, class: `form-field-${formFieldType}`},
            {value: fieldBorderHoverHeight, class: `field-border-hover-${fieldBorderHoverHeight}px`},
            {value: fieldBorderHeight, class: `field-border-${fieldBorderHeight}px`},
            {value: compact, class: `form-field-${compact}`},
        ]

        classes.forEach((el: any) => {
            el.value !== 'none' && document.querySelector('body')?.classList.add(el.class)
        })

    },[fieldBorderColor, formFieldType, fieldBorderHoverHeight, fieldBorderHeight, compact, userSettings])

    useEffect(() => {
        const colors = [
            {value: gridHeaderColor, color: '--ix-grid-header-color'},
            {value: gridHeaderTextColor, color: '--ix-grid-header-text-color'},
            {value: gridRowColor, color: '--ix-grid-row-color'},
            {value: gridRowTextColor, color: '--ix-grid-row-text-color'},
            {value: gridBordersColor, color: '--ix-grid-borders-color'},
        ]

        colors.forEach((el: any) => {
            document.querySelector('body')?.style.setProperty(el.color, el.value)
        })

    },[gridHeaderColor, gridHeaderTextColor, gridRowColor, gridRowTextColor, gridBordersColor])

    // fetch settings and fetch them again if user changes
    useEffect(() => {
        // if no user is logged in we don't fetch anything
        if (!user) {
            settings && setSettings(null)
            dispatch(resetSettings())
            return
        }

        if (!settings) {
            client.get('/api/ixweb/current-settings/')
                .then((res: any) => {
                    setSettings(res.data.user_settings)
                })
                .catch((_: any) => {
                    notify('Failed to fetch user settings')
                })
            client.get(`/api/ixweb/ixusersettings/?action=gte&USER_ID=${user?.ixUserId}&SETTINGTYPE=0`).then((res: any) => {
                res.data.forEach((setting: any) => {
                    const value = setting.SETTINGDATA_1
                    dispatch(setUserSettings({
                        settingName: setting.SETTINGNAME,
                        value: value !== 'null'
                            ? !isNaN(value)
                                ? Number(value) 
                                : setting.SETTINGKEY === 'form-layout' || setting.SETTINGKEY === 'common-run-time' ? JSON.parse(value) : value
                            : null,
                        id: setting.HDR_ID,
                        settingKey: setting.SETTINGKEY
                    }))
                })
            }).catch(() => console.error('Failed to load settings'))
        }
    }, [user, settings, dispatch])

    // pass loaded settings/rights to utils singleton
    useEffect(() => {
        utils.setLicenses([
            License.eMITGLIEDERVERWALTUNG,
            License.eKAUTION_ZAHLUNGSVERKEHR_DKB,
            License.eVIRTUELLE_KONTEN,
            License.eKOMFORTPAKET,
        ])
    }, [])

    useEffect(() => {
        if (user) {
            // set user in utils singleton
            utils.setUser(user)
        }
    }, [user])

    // fetch  module rights based on user's klasse
    useEffect(() => {
        if (user) {
            dispatch(fetchAllRights(user.klasse))
            dispatch(fetchLNR())
            dispatch(fetchActiveLicenses())
        }
    }, [dispatch, user])

    // show some loading indicator while waiting for settings to load 1019 659
    if (user && (!settings || rightsStatus !== 'loaded')) {
        return <FullPageSpinner />
    }

    return (
        <Drawer>
            <IxSpotlight />
            <Settings settings={settings} />
            <IxGlobalConfirmPopup />
            <IxJobsUpdater />
            <Routes>
                {
                    routes.map((route, i) => (
                        <Route
                            key={i}
                            path={route.path}
                            element={
                                <Can
                                    prg={ route.prg }
                                    yes={ <route.component /> }
                                    no={ <NoAccess /> }
                                />
                            }
                        />
                    ))
                }
            </Routes>
        </Drawer>
    )
}

export const App = () => {
    const { user } = useIxAuth()
    const dispatch = useAppDispatch()

    useEffect(() => {
        if (user) { dispatch(userLoggedIn()) }
        else if (!user) { dispatch(userLoggedOut()) }
    }, [dispatch, user])

    return (
        <Router>
            <React.Suspense fallback={<FullPageSpinner />}>
                <div className='app' style={{height: '100%', width: '100%'}}>
                    <Routes>
                        <Route
                            path='/login'
                            element={
                                <Can
                                    prg='V_LOGIN'
                                    yes={ <Login /> }
                                    no={ <AlreadyLoggedIn /> }
                                />
                            }
                        />
                        <Route
                            path='/sso-login'
                            element={
                                <Can
                                    prg='V_SSO_LOGIN'
                                    yes={ <SsoLogin /> }
                                    no={ <AlreadyLoggedIn /> }
                                />
                            }
                        />
                        <Route
                            path='/sso-redirect'
                            element={
                                <Can
                                    prg='V_SSO_REDIRECT'
                                    yes={ <SsoRedirect /> }
                                    no={ <AlreadyLoggedIn /> }
                                />
                            }
                        />

                        <Route path='/*' element={ <MainPage /> } />
                    </Routes>
                </div>
            </React.Suspense>
        </Router>
    )
}
